import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import LoadingBuffer from "../components/LoadingBuffer";
import {
  addGuestCartItem,
  getGuestCart,
  getProductColours,
  getProductCartQty,
  incrementCartQty,
  decrementCartQty,
  syncUserCartCache,
  handleUniversalAddToCart,
  getUnifiedCart
} from "../utils/cartHelper";

const API_BASE = "https://api.vanyacollectionsrajahmundry.com";

const ProductdetailedPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // ============================================================
  // STATE
  // ============================================================

  const [quantity, setQuantity] = useState(1);
  const [cartQty, setCartQty] = useState(0);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState("description");

  // Main gallery image
  const [mainImage, setMainImage] = useState("");

  // Selected colour
  const [selectedColour, setSelectedColour] = useState(null);

  // Selected size for dresses
  const [selectedSize, setSelectedSize] = useState(null);

  // Sync cart quantity
  useEffect(() => {
    const syncQty = () => {
      if (!product?.id) return;
      const q = getProductCartQty(product.id, selectedColour, selectedSize);
      setCartQty(q);
    };
    syncQty();
    window.addEventListener("vanya_cart_change", syncQty);
    window.addEventListener("storage", syncQty);
    return () => {
      window.removeEventListener("vanya_cart_change", syncQty);
      window.removeEventListener("storage", syncQty);
    };
  }, [product?.id, selectedColour, selectedSize]);

  const [relatedProducts, setRelatedProducts] = useState([]);

  const [reviews, setReviews] = useState([]);

  const [wishlisted, setWishlisted] = useState(false);

  const [newReview, setNewReview] = useState({
    rating: 5,
    comment: "",
  });

  // ============================================================
  // HELPERS
  // ============================================================

  const cleanImageUrl = (url) => {
    if (!url || typeof url !== "string") return "";

    // Ignore local browser blob images
    if (url.startsWith("blob:")) return "";

    return url.trim();
  };

  const getProductVariants = () => {
    if (!Array.isArray(product?.variants)) return [];

    return product.variants.filter(Boolean);
  };

 const isDressProduct = useMemo(() => {
  if (!product) return false;

  const category = String(product.category || "")
    .trim()
    .toLowerCase();

  return (
    category.includes("dress") ||
    category.includes("gown") ||
    category.includes("kurti") ||
    category.includes("salwar") ||
    category.includes("material")
  );
}, [product]);

  // ============================================================
  // SELECTED VARIANT
  // ============================================================

const selectedVariant = useMemo(() => {
  if (!product) return null;

  const variants = getProductVariants();
  if (variants.length === 0) return null;

  const colours = getProductColours(product);
  const targetColour = selectedColour || (colours.length === 1 ? colours[0] : null);

  if (!targetColour) return null;

  return (
    variants.find(
      (variant) =>
        String(variant.colour || "")
          .trim()
          .toLowerCase() ===
        String(targetColour || "")
          .trim()
          .toLowerCase()
    ) || variants[0] || null
  );
}, [product, selectedColour]);
  // ============================================================
  // SELECTED SIZE
  // ============================================================

const selectedSizeData = useMemo(() => {
  if (!selectedVariant || !selectedSize) {
    return null;
  }

  if (!Array.isArray(selectedVariant.sizes)) {
    return null;
  }

  return (
    selectedVariant.sizes.find(
      (sizeItem) =>
        String(sizeItem.size || "")
          .trim()
          .toLowerCase() ===
        String(selectedSize || "")
          .trim()
          .toLowerCase()
    ) || null
  );
}, [selectedVariant, selectedSize]);
  // ============================================================
  // CURRENT PRICE
  // ============================================================

  const currentPrice = useMemo(() => {
    if (!product) return 0;

    // Dress + selected size
    if (isDressProduct && selectedSizeData) {
      return Number(selectedSizeData.price || 0);
    }

    // Colour variant
    if (selectedVariant) {
      if (selectedVariant.price != null && Number(selectedVariant.price) > 0) {
        return Number(selectedVariant.price);
      }
      if (Array.isArray(selectedVariant.sizes) && selectedVariant.sizes.length > 0) {
        return Number(selectedVariant.sizes[0]?.price || product.price || 0);
      }
      return Number(selectedVariant.price ?? product.price ?? 0);
    }

    // Main product
    return Number(product.price || 0);
  }, [
    product,
    selectedVariant,
    selectedSizeData,
    isDressProduct,
  ]);

  // ============================================================
  // CURRENT OLD PRICE
  // ============================================================

  const currentOldPrice = useMemo(() => {
    if (!product) return 0;

    // Dress + selected size
    if (isDressProduct && selectedSizeData) {
      return Number(selectedSizeData.oldPrice || selectedSizeData.old_price || 0);
    }

    // Colour variant
    if (selectedVariant) {
      if (selectedVariant.oldPrice != null && Number(selectedVariant.oldPrice) > 0) {
        return Number(selectedVariant.oldPrice);
      }
      if (Array.isArray(selectedVariant.sizes) && selectedVariant.sizes.length > 0) {
        return Number(
          selectedVariant.sizes[0]?.oldPrice ||
            selectedVariant.sizes[0]?.old_price ||
            product.old_price ||
            0
        );
      }
      return Number(selectedVariant.oldPrice ?? selectedVariant.old_price ?? product.old_price ?? 0);
    }

    return Number(product.old_price || 0);
  }, [
    product,
    selectedVariant,
    selectedSizeData,
    isDressProduct,
  ]);

  // ============================================================
  // CURRENT DISCOUNT
  // ============================================================

  const currentDiscount = useMemo(() => {
    if (!product) return 0;

    // Dress + selected size
    if (isDressProduct && selectedSizeData) {
      if (selectedSizeData.discount != null) {
        return Number(selectedSizeData.discount || 0);
      }
    }

    // Colour variant
    if (selectedVariant) {
      if (selectedVariant.discount != null) {
        return Number(selectedVariant.discount || 0);
      }
    }

    // Main product
    if (product.discount != null) {
      return Number(product.discount || 0);
    }

    if (currentOldPrice && currentPrice) {
      return Math.round(
        ((currentOldPrice - currentPrice) / currentOldPrice) * 100
      );
    }

    return 0;
  }, [
    product,
    selectedVariant,
    selectedSizeData,
    isDressProduct,
    currentOldPrice,
    currentPrice,
  ]);

  // ============================================================
  // CURRENT STOCK
  // ============================================================

const currentStock = useMemo(() => {
  if (!product) return 0;

  // ==========================================================
  // DRESS MATERIAL / DRESS WITH SIZE STOCK
  // ==========================================================

  if (
    isDressProduct &&
    selectedVariant &&
    Array.isArray(selectedVariant.sizes) &&
    selectedVariant.sizes.length > 0
  ) {
    // A size is selected
    if (selectedSizeData) {
      return Number(selectedSizeData.stock || 0);
    }

    // Colour selected but size not selected
    // Add all size stocks
    return selectedVariant.sizes.reduce(
      (total, sizeItem) =>
        total + Number(sizeItem.stock || 0),
      0
    );
  }

  // ==========================================================
  // NORMAL SAREE COLOUR VARIANT
  // ==========================================================

  if (selectedVariant) {
    return Number(
      selectedVariant.stock ??
        product.stock ??
        0
    );
  }

  // ==========================================================
  // MAIN PRODUCT
  // ==========================================================

  return Number(product.stock || 0);
}, [
  product,
  selectedVariant,
  selectedSizeData,
  isDressProduct,
]);
  // ============================================================
  // TOTAL PRICE
  // ============================================================

  const totalPrice = currentPrice * quantity;

  // ============================================================
  // REVIEWS
  // ============================================================

  const averageRating = reviews.length
    ? reviews.reduce(
        (sum, review) => sum + Number(review.rating || 0),
        0
      ) / reviews.length
    : 0;

  const roundedRating =
    Math.round(averageRating * 2) / 2;

  const totalReviews = reviews.length;

  const fullStars = "★".repeat(
    Math.floor(roundedRating)
  );

  const halfStar =
    roundedRating % 1 === 0.5 ? "½" : "";

  const emptyStars = "☆".repeat(
    5 - Math.ceil(roundedRating)
  );

  const starDisplay =
    fullStars + halfStar + emptyStars;

  // ============================================================
  // FETCH REVIEWS
  // ============================================================

  const fetchReviews = async () => {
    try {
      const res = await fetch(
        `${API_BASE}/review/${id}`
      );

      const data = await res.json();

      setReviews(
        Array.isArray(data)
          ? data
          : data.reviews ||
              data.data ||
              []
      );
    } catch (err) {
      console.error("Review fetch error:", err);
    }
  };

  // ============================================================
  // FETCH PRODUCT
  // ============================================================

  useEffect(() => {
    const normalizeCat = (v = "") =>
      String(v || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");

    const fetchProductData = async () => {
      try {
        setLoading(true);

        const [res, catRes] = await Promise.all([
          fetch(`${API_BASE}/products/all`),
          fetch(`${API_BASE}/categories`).catch(() => null),
        ]);

        const data = await res.json();
        const catData = catRes && catRes.ok ? await catRes.json() : [];
        const apiCategories = Array.isArray(catData) ? catData : [];

        const products = Array.isArray(data)
          ? data
          : data.products ||
            data.data ||
            [];

        const rawSelectedProduct = products.find(
          (item) => item.id === Number(id)
        );

        if (!rawSelectedProduct || rawSelectedProduct.is_hold) {
          setProduct(null);
          return;
        }

        // Reconcile category display name with /categories
        let resolvedCat = rawSelectedProduct.category || "";
        const normRawCat = normalizeCat(resolvedCat);
        const matchedCat = apiCategories.find(
          (c) => normalizeCat(c?.category) === normRawCat
        );
        if (matchedCat?.category) {
          resolvedCat = matchedCat.category;
        }

        const selectedProduct = {
          ...rawSelectedProduct,
          category: resolvedCat,
        };

        setProduct(selectedProduct);

        // Initial image falls back to variant mainImage or product thumbnails if img_url is null
        const initialMainImg =
          cleanImageUrl(selectedProduct.img_url) ||
          cleanImageUrl(selectedProduct.variants?.[0]?.mainImage) ||
          cleanImageUrl(selectedProduct.thumbnails?.[0]) ||
          cleanImageUrl(selectedProduct.variants?.[0]?.thumbnails?.[0]) ||
          "";

        setMainImage(initialMainImg);

        // Auto-select colour if only 1 colour is available
        const availColours = getProductColours(selectedProduct);
        if (availColours.length === 1) {
          setSelectedColour(availColours[0]);
          const firstVar = Array.isArray(selectedProduct.variants)
            ? selectedProduct.variants[0]
            : null;
          if (Array.isArray(firstVar?.sizes) && firstVar.sizes.length === 1) {
            setSelectedSize(firstVar.sizes[0].size || null);
          } else {
            setSelectedSize(null);
          }
        } else {
          setSelectedColour(null);
          setSelectedSize(null);
        }
        setQuantity(1);

        // Related products (normalized category match)
        const related = products
          .filter(
            (item) =>
              !item.is_hold &&
              normalizeCat(item.category) ===
                normalizeCat(selectedProduct.category) &&
              item.id !== selectedProduct.id
          )
          .slice(0, 4);

        setRelatedProducts(related);
      } catch (err) {
        console.error(
          "Product fetch error:",
          err
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
    fetchReviews();

    window.scrollTo(0, 0);
  }, [id]);

  // ============================================================
  // VARIANT IMAGES
  // ============================================================

  const variantImages = useMemo(() => {
    if (!selectedVariant) return [];

    const images = [];

    // Variant main image first
    const variantMain = cleanImageUrl(
      selectedVariant.mainImage
    );

    if (variantMain) {
      images.push(variantMain);
    }

    // Variant thumbnails
    if (Array.isArray(selectedVariant.thumbnails)) {
      selectedVariant.thumbnails.forEach((image) => {
        const cleaned = cleanImageUrl(image);

        if (
          cleaned &&
          !images.includes(cleaned)
        ) {
          images.push(cleaned);
        }
      });
    }

    return images;
  }, [selectedVariant]);

  // ============================================================
  // DEFAULT PRODUCT IMAGES
  // ============================================================

  const productImages = useMemo(() => {
    if (!product) return [];

    const images = [];

    const main = cleanImageUrl(
      product.img_url
    );

    if (main) {
      images.push(main);
    }

    if (Array.isArray(product.thumbnails)) {
      product.thumbnails.forEach((image) => {
        const cleaned = cleanImageUrl(image);

        if (
          cleaned &&
          !images.includes(cleaned)
        ) {
          images.push(cleaned);
        }
      });
    }

    return images;
  }, [product]);

  // ============================================================
  // CURRENT THUMBNAILS
  // ============================================================

  const thumbnails =
    selectedVariant && variantImages.length
      ? variantImages
      : productImages;

  // ============================================================
  // COLOUR SELECT
  // ============================================================

  const handleColourSelect = (colour) => {
    const variants = getProductVariants();

    const variant = variants.find(
      (item) =>
        String(item.colour || "").toLowerCase() ===
        String(colour || "").toLowerCase()
    );

    if (!variant) return;

    setSelectedColour(colour);

    // Auto-select size if only 1 size exists on this colour variant, otherwise reset
    if (Array.isArray(variant.sizes) && variant.sizes.length === 1) {
      setSelectedSize(variant.sizes[0]?.size || null);
    } else {
      setSelectedSize(null);
    }

    // Reset quantity
    setQuantity(1);

    // IMPORTANT:
    // Clicking GREEN immediately changes main image
    const newMainImage = cleanImageUrl(
      variant.mainImage
    );

    if (newMainImage) {
      setMainImage(newMainImage);
    }
  };

  // ============================================================
  // SIZE SELECT
  // ============================================================

  const handleSizeSelect = (size) => {
    setSelectedSize(size);
    setQuantity(1);

    // Keep currently selected colour image
    if (
      selectedVariant &&
      cleanImageUrl(selectedVariant.mainImage)
    ) {
      setMainImage(
        cleanImageUrl(
          selectedVariant.mainImage
        )
      );
    }
  };
// ============================================================
// GET EXACT SELECTED VARIANT FOR CART
// ============================================================

const getSelectedVariantForCart = () => {
  const variants = getProductVariants();

  console.log("ALL VARIANTS:", variants);
  console.log("SELECTED COLOUR:", selectedColour);
  console.log("SELECTED SIZE:", selectedSize);
  console.log("SELECTED VARIANT:", selectedVariant);
  console.log("SELECTED SIZE DATA:", selectedSizeData);

  const productColours = getProductColours(product);
  const activeColour = selectedColour || (productColours.length === 1 ? productColours[0] : null);

  if (variants.length === 0) {
    return {
      id: null,
      variant_id: null,
      colour: activeColour || product?.colour || null,
      size: selectedSize || null,
      price: Number(currentPrice || product?.price || 0),
      old_price: Number(currentOldPrice || product?.old_price || 0),
      oldPrice: Number(currentOldPrice || product?.old_price || 0),
      discount: Number(currentDiscount || product?.discount || 0),
      stock: Number(product?.stock ?? 0),
      img_url: cleanImageUrl(product?.img_url) || "",
      mainImage: cleanImageUrl(product?.img_url) || "",
      thumbnails: [],
      sizes: [],
    };
  }

  // Find variant directly from selected colour or active colour
  const exactVariant =
    selectedVariant ||
    (activeColour
      ? variants.find(
          (variant) =>
            String(variant.colour || "")
              .trim()
              .toLowerCase() ===
            String(activeColour || "")
              .trim()
              .toLowerCase()
        )
      : null) ||
    (productColours.length <= 1 ? variants[0] : null);

  if (!exactVariant) {
    return null;
  }

  // Selected size data
  let exactSize = null;

  if (
    selectedSize &&
    Array.isArray(exactVariant.sizes)
  ) {
    exactSize =
      exactVariant.sizes.find(
        (item) =>
          String(item.size || "")
            .trim()
            .toLowerCase() ===
          String(selectedSize || "")
            .trim()
            .toLowerCase()
      ) || null;
  }

  // ==========================================================
  // IMPORTANT
  // ==========================================================

  const variantImage =
    cleanImageUrl(
      exactVariant.mainImage
    ) ||
    cleanImageUrl(
      exactVariant.image
    ) ||
    cleanImageUrl(
      exactVariant.img_url
    ) ||
    cleanImageUrl(
      product?.img_url
    );

  const variantPrice =
    Number(
      exactSize?.price ??
        exactVariant.price ??
        product?.price ??
        0
    );

  const variantStock =
    Number(
      exactSize?.stock ??
        exactVariant.stock ??
        product?.stock ??
        0
    );

  const variantOldPrice =
    Number(
      exactSize?.oldPrice ??
        exactSize?.old_price ??
        exactVariant.oldPrice ??
        exactVariant.old_price ??
        product?.old_price ??
        0
    );

  const variantDiscount =
    Number(
      exactSize?.discount ??
        exactVariant.discount ??
        product?.discount ??
        0
    );

  const result = {
    // Variant identity
    id:
      exactVariant.id ??
      exactVariant.variant_id ??
      null,

    variant_id:
      exactVariant.id ??
      exactVariant.variant_id ??
      null,

    colour:
      exactVariant.colour ||
      selectedColour ||
      null,

    size:
      selectedSize ||
      exactSize?.size ||
      null,

    // Variant pricing
    price: variantPrice,

    old_price: variantOldPrice,

    oldPrice: variantOldPrice,

    discount: variantDiscount,

    stock: variantStock,

    // Variant image
    img_url: variantImage,

    mainImage: variantImage,

    thumbnails:
      Array.isArray(
        exactVariant.thumbnails
      )
        ? exactVariant.thumbnails
        : [],

    // Keep original data too
    sizes:
      Array.isArray(
        exactVariant.sizes
      )
        ? exactVariant.sizes
        : [],
  };

  console.log(
    "FINAL VARIANT TO CART:",
    result
  );

  return result;
};

// ============================================================
// ADD TO CART
// ============================================================

const addToCart = async (
  productId,
  qty = 1
) => {
  try {
    const productColours = getProductColours(product);
    let activeColour = selectedColour;

    if (
      productColours.length > 1 &&
      !activeColour
    ) {
      alert(
        "Please select a colour first."
      );
      return;
    }

    if (!activeColour && productColours.length === 1) {
      activeColour = productColours[0];
      setSelectedColour(productColours[0]);
    }

    let activeSize = selectedSize;
    if (
      !activeSize &&
      Array.isArray(selectedVariant?.sizes) &&
      selectedVariant.sizes.length === 1
    ) {
      activeSize = selectedVariant.sizes[0]?.size || null;
      setSelectedSize(activeSize);
    }

    if (
      isDressProduct &&
      selectedVariant?.sizes?.length > 1 &&
      !activeSize
    ) {
      alert(
        "Please select a size first."
      );
      return;
    }

    const cartVariant = getSelectedVariantForCart();
    const variantStock = Number(cartVariant?.stock ?? currentStock ?? 0);

    if (variantStock <= 0) {
      alert("This selected product/variant is out of stock.");
      return;
    }

    if (cartQty + qty > variantStock) {
      alert(
        cartQty > 0
          ? `You already have ${cartQty} item(s) in your bag. Only ${variantStock} available in stock.`
          : `Only ${variantStock} item(s) available for the selected variant.`
      );
      return;
    }

    const res = await handleUniversalAddToCart(product, {
      colour: activeColour,
      size: activeSize || cartVariant?.size || null,
      variant: cartVariant,
      quantity: Number(qty || 1),
    });

    if (!res.success) {
      alert(res.message || "Could not add item to bag.");
      return;
    }

    setQuantity(1);
  } catch (err) {
    console.error("Add cart error:", err);
    alert("Error adding to cart: " + err.message);
  }
};

  // ============================================================
  // BUY NOW
  // ============================================================

  const handleBuyNow = async () => {
    // Require colour
    const productColours = getProductColours(product);
    let activeColour = selectedColour;

    if (
      productColours.length > 1 &&
      !activeColour
    ) {
      alert(
        "Please select a colour first."
      );
      return;
    }

    if (!activeColour && productColours.length === 1) {
      activeColour = productColours[0];
      setSelectedColour(productColours[0]);
    }

    let activeSize = selectedSize;
    if (
      !activeSize &&
      Array.isArray(selectedVariant?.sizes) &&
      selectedVariant.sizes.length === 1
    ) {
      activeSize = selectedVariant.sizes[0]?.size || null;
      setSelectedSize(activeSize);
    }

    // Require size if multiple sizes exist
    if (
      isDressProduct &&
      selectedVariant?.sizes?.length > 1 &&
      !activeSize
    ) {
      alert(
        "Please select a size first."
      );
      return;
    }

    const availableStock = Number(currentStock ?? 0);

    if (availableStock <= 0) {
      alert("This product/variant is currently out of stock.");
      return;
    }

    if (quantity > availableStock) {
      alert(
        `Only ${availableStock} item(s) available.`
      );
      return;
    }

    try {
      const cartVariant = getSelectedVariantForCart();

      // If not already in bag, add it first using unified helper
      if (cartQty <= 0) {
        const addRes = await handleUniversalAddToCart(product, {
          colour: activeColour,
          size: activeSize || cartVariant?.size || null,
          variant: cartVariant,
          quantity,
        });
        if (!addRes.success && !addRes.maxReached) {
          alert(addRes.message || "Failed to add to cart");
          return;
        }
      }

      const cartItems = getUnifiedCart();
      if (!cartItems.length) {
        alert("Cart is empty. Please try again.");
        return;
      }

      const totalAmount = cartItems.reduce((acc, item) => {
        const price = Number(item.variant?.price ?? item.price ?? item.product?.price ?? 0);
        const qty = Number(item.quantity ?? item.qty ?? 1);
        return acc + price * qty;
      }, 0);

      navigate("/checkout", {
        state: {
          items: cartItems,
          totalAmount,
        },
      });
    } catch (err) {
      console.error("Buy now error:", err);
      alert("Error processing your request: " + err.message);
    }
  };

  // ============================================================
  // WISHLIST
  // ============================================================

  const addWishlist = async () => {
    try {
      const storedUser =
        localStorage.getItem("user");

      if (!storedUser) {
        alert(
          "Please login to manage your wishlist"
        );
        navigate("/login");
        return;
      }

      const user =
        JSON.parse(storedUser);

      const res = await fetch(
        `${API_BASE}/review/wishlist/add`,
        {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json",
          },
          body: JSON.stringify({
            user_id: user.id,
            product_id: product.id,
          }),
        }
      );

      const data =
        await res.json();

      if (!res.ok) {
        throw new Error(
          data.error ||
            "Wishlist action failed"
        );
      }

      setWishlisted(true);

      alert(
        "Added to wishlist ❤️"
      );
    } catch (err) {
      console.error(err);

      alert(
        "Wishlist action failed"
      );
    }
  };

  // ============================================================
  // ADD REVIEW
  // ============================================================

  const handleAddReview = async () => {
    const storedUser =
      localStorage.getItem("user");

    if (!storedUser) {
      alert(
        "Please login to write a review"
      );
      navigate("/login");
      return;
    }

    const user =
      JSON.parse(storedUser);

    try {
      const res = await fetch(
        `${API_BASE}/review/add`,
        {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json",
          },
          body: JSON.stringify({
            user_id: user.id,
            product_id: product.id,
            rating:
              newReview.rating,
            comment:
              newReview.comment,
          }),
        }
      );

      const data =
        await res.json();

      if (!res.ok) {
        throw new Error(
          data.error ||
            "Error adding review"
        );
      }

      setReviews([
        data,
        ...reviews,
      ]);

      setNewReview({
        rating: 5,
        comment: "",
      });

      alert(
        "Review submitted successfully! 🌟"
      );
    } catch (err) {
      console.error(err);

      alert(
        "Error adding review"
      );
    }
  };

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <LoadingBuffer 
        message="Loading Handcrafted Saree Details..." 
        fullPage={true} 
      />
    );
  }

  if (!product) {
    return (
      <div style={{ minHeight: '75vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '40px 20px', fontFamily: "'Playfair Display', serif" }}>
        <p style={{ fontSize: '24px', color: '#522b5b', marginBottom: '16px', fontWeight: 600 }}>Masterpiece Not Found</p>
        <p style={{ fontFamily: "'Montserrat', sans-serif", color: '#666', fontSize: '14px', marginBottom: '24px' }}>The product you are looking for might have been sold or moved.</p>
        <button 
          onClick={() => navigate('/shop')} 
          style={{ background: 'linear-gradient(135deg, #fceea7 0%, #e2bc53 50%, #c99b27 100%)', border: 'none', color: '#2a1236', padding: '14px 34px', borderRadius: '999px', fontWeight: 700, fontSize: '13px', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '1px' }}
        >
          Explore All Sarees
        </button>
      </div>
    );
  }

  // ============================================================
  // COLOUR VARIANTS
  // ============================================================

  const variants =
    getProductVariants();

  const colours = getProductColours(product);

  // ============================================================
  // SIZE VARIANTS
  // ============================================================

  const sizes =
    selectedVariant &&
    Array.isArray(
      selectedVariant.sizes
    )
      ? selectedVariant.sizes
      : [];

  return (
    <div className="pd-container">
      <style>{styles}</style>

      {/* ======================================================
          BREADCRUMB
      ====================================================== */}

      <div className="pd-breadcrumb">
        <Link to="/">
          Home
        </Link>

        &nbsp;›&nbsp;

        <Link to="/shop">
          Shop
        </Link>

        &nbsp;›&nbsp;

        <span>
          {product.name}
        </span>
      </div>

      <div className="pd-wrapper">

        {/* ====================================================
            LEFT SIDE
        ==================================================== */}

        <div className="pd-left">

          <div className="pd-main-img-container">

            <img
              src={
                mainImage ||
                cleanImageUrl(
                  product.img_url
                )
              }
              alt={product.name}
              className="pd-main-img"
            />

            {currentDiscount > 0 && (
              <span className="pd-floating-badge">
                -{currentDiscount}% OFF
              </span>
            )}
          </div>

          {/* ==================================================
              THUMBNAILS
          ================================================== */}

          <div className="pd-thumbnails">

            {thumbnails.map(
              (img, idx) => (
                <img
                  key={`${img}-${idx}`}
                  src={img}
                  alt={`${product.name} ${
                    idx + 1
                  }`}
                  className={`pd-thumb ${
                    mainImage === img
                      ? "active-thumb"
                      : ""
                  }`}
                  onClick={() =>
                    setMainImage(img)
                  }
                />
              )
            )}

          </div>
        </div>

        {/* ====================================================
            RIGHT SIDE
        ==================================================== */}

        <div className="pd-right">

          <p className="pd-category">
            {product.category ||
              "Handcrafted Heritage"}
          </p>

          <h1 className="pd-title">
            {product.name}
          </h1>

          {/* ==================================================
              RATING
          ================================================== */}

          <div className="pd-rating">
            <span className="stars">
              {starDisplay}
            </span>

            <span className="rating-text">
              {averageRating.toFixed(1)}{" "}
              ({totalReviews} verified
              review
              {totalReviews !== 1
                ? "s"
                : ""}
              )
            </span>
          </div>

          {/* ==================================================
              PRICE
          ================================================== */}

          <div className="pd-price-row">

            <span className="pd-price">
              ₹
              {Number(
                totalPrice
              ).toLocaleString()}
            </span>

            {currentOldPrice > 0 && (
              <>
                <span className="pd-old">
                  ₹
                  {Number(
                    currentOldPrice
                  ).toLocaleString()}
                </span>

                {currentDiscount >
                  0 && (
                  <span className="pd-discount">
                    Save{" "}
                    {
                      currentDiscount
                    }
                    %
                  </span>
                )}
              </>
            )}

          </div>

          {/* ==================================================
              COLOUR VARIANTS
          ================================================== */}

          {colours.length > 0 && (
            <div className="pd-variant-section">

              <div className="pd-variant-heading">
                <span>
                  Colour:
                </span>

                <strong>
                  {selectedColour ||
                    "Select Colour"}
                </strong>
              </div>

              <div className="pd-colour-list">

                {colours.map(
                  (colour) => {
                    const variant =
                      variants.find(
                        (item) =>
                          String(
                            item.colour ||
                              ""
                          ).toLowerCase() ===
                          String(
                            colour ||
                              ""
                          ).toLowerCase()
                      );

                    const variantImage =
                      cleanImageUrl(
                        variant?.mainImage
                      );

                    const isSelected =
                      String(
                        selectedColour ||
                          ""
                      ).toLowerCase() ===
                      String(
                        colour ||
                          ""
                      ).toLowerCase();

                    return (
                      <button
                        key={colour}
                        type="button"
                        className={`pd-colour-option ${
                          isSelected
                            ? "selected-colour"
                            : ""
                        }`}
                        onClick={() =>
                          handleColourSelect(
                            colour
                          )
                        }
                        title={colour}
                      >

                        <span
                          className="pd-colour-image-wrap"
                        >
                          {variantImage ? (
                            <img
                              src={
                                variantImage
                              }
                              alt={
                                colour
                              }
                            />
                          ) : (
                            <span
                              className="pd-colour-dot"
                              style={{
                                background:
                                  colour.toLowerCase(),
                              }}
                            />
                          )}
                        </span>

                        <span className="pd-colour-name">
                          {String(
                            colour
                          )
                            .charAt(0)
                            .toUpperCase() +
                            String(
                              colour
                            ).slice(
                              1
                            )}
                        </span>

                      </button>
                    );
                  }
                )}

              </div>
            </div>
          )}

          {/* ==================================================
              DRESS SIZES
          ================================================== */}

          {isDressProduct &&
            selectedVariant &&
            sizes.length > 0 && (
              <div className="pd-size-section">

                <div className="pd-variant-heading">
                  <span>
                    Size:
                  </span>

                  <strong>
                    {selectedSize ||
                      "Select Size"}
                  </strong>
                </div>

                <div className="pd-size-list">

                  {sizes.map(
                    (sizeItem) => {
                      const isSelected =
                        selectedSize ===
                        sizeItem.size;

                      const outOfStock =
                        Number(
                          sizeItem.stock ||
                            0
                        ) <= 0;

                      return (
                        <button
                          key={
                            sizeItem.size
                          }
                          type="button"
                          disabled={
                            outOfStock
                          }
                          className={`pd-size-btn ${
                            isSelected
                              ? "selected-size"
                              : ""
                          } ${
                            outOfStock
                              ? "size-out-stock"
                              : ""
                          }`}
                          onClick={() =>
                            handleSizeSelect(
                              sizeItem.size
                            )
                          }
                        >
                          {
                            sizeItem.size
                          }
                        </button>
                      );
                    }
                  )}

                </div>
              </div>
            )}

          {/* ==================================================
              STOCK
          ================================================== */}

          <div className="pd-stock">

            {currentStock > 0 ? (
              <span className="stock-badge in-stock">
                ✓ In Stock (
                {currentStock}{" "}
                pieces available)
              </span>
            ) : (
              <span className="stock-badge out-stock">
                ✕ Currently Out of Stock
              </span>
            )}

          </div>

          <hr className="pd-divider" />

          {/* ==================================================
              SPECS
          ================================================== */}

          {/* ==================================================
              SPECS (Declared by Admin only)
          ================================================== */}

          {(product?.fabric || product?.length || product?.occasion || product?.authenticity) && (
            <div className="pd-spec-grid">

              {product?.fabric && (
                <div className="pd-spec-box">
                  <span>
                    FABRIC
                  </span>

                  <p>
                    {product.fabric}
                  </p>
                </div>
              )}

              {product?.length && (
                <div className="pd-spec-box">
                  <span>
                    LENGTH
                  </span>

                  <p>
                    {product.length}
                  </p>
                </div>
              )}

              {product?.occasion && (
                <div className="pd-spec-box">
                  <span>
                    OCCASION
                  </span>

                  <p>
                    {product.occasion}
                  </p>
                </div>
              )}

              {product?.authenticity && (
                <div className="pd-spec-box">
                  <span>
                    AUTHENTICITY
                  </span>

                  <p>
                    {product.authenticity}
                  </p>
                </div>
              )}

            </div>
          )}

          {/* ==================================================
              QUANTITY
          ================================================== */}

          <div className="pd-qty-row">

            <span className="pd-qty-label">
              Quantity:
            </span>

            <div className="pd-qty">

              <button
                onClick={() =>
                  setQuantity(
                    Math.max(
                      1,
                      quantity - 1
                    )
                  )
                }
              >
                −
              </button>

              <span>
                {quantity}
              </span>

              <button
                onClick={() =>
                  setQuantity(
                    Math.min(
                      currentStock ||
                        1,
                      quantity + 1
                    )
                  )
                }
                disabled={
                  quantity >=
                  currentStock
                }
              >
                +
              </button>

            </div>
          </div>

          {/* ==================================================
              ACTIONS
          ================================================== */}

          <div className="pd-action-section">

            {/* ALREADY IN BAG BANNER & STEPPER */}
            {cartQty > 0 && (
              <div className="pd-in-cart-banner" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'linear-gradient(135deg, #fdfaf6 0%, #f7edf8 100%)',
                border: '1.5px solid #d4af37',
                borderRadius: '14px',
                padding: '12px 18px',
                marginBottom: '10px',
                boxShadow: '0 4px 15px rgba(92, 32, 105, 0.08)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '18px' }}>🛍️</span>
                  <div>
                    <strong style={{ color: '#4b2954', fontSize: '14px', display: 'block' }}>
                      {cartQty} {cartQty === 1 ? 'item' : 'items'} in your Bag
                    </strong>
                    <span style={{ fontSize: '11px', color: '#7e5a87' }}>
                      {selectedColour ? `Colour: ${selectedColour} ` : ''}{selectedSize ? `Size: ${selectedSize}` : ''}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button
                    type="button"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      border: '1px solid #4b2954',
                      background: '#ffffff',
                      color: '#4b2954',
                      fontSize: '16px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onClick={async () => {
                      await decrementCartQty(product, { colour: selectedColour, size: selectedSize, variant: selectedVariant });
                    }}
                  >
                    −
                  </button>
                  <span style={{ fontWeight: '700', fontSize: '15px', color: '#4b2954', minWidth: '20px', textAlign: 'center' }}>
                    {cartQty}
                  </span>
                  <button
                    type="button"
                    disabled={cartQty >= currentStock}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      border: '1px solid #4b2954',
                      background: cartQty >= currentStock ? '#eee' : '#4b2954',
                      color: cartQty >= currentStock ? '#888' : '#fbeea4',
                      fontSize: '16px',
                      fontWeight: '700',
                      cursor: cartQty >= currentStock ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onClick={async () => {
                      if (cartQty >= currentStock) {
                        alert(`Only ${currentStock} item(s) available in stock.`);
                        return;
                      }
                      await incrementCartQty(product, { colour: selectedColour, size: selectedSize, variant: selectedVariant });
                    }}
                  >
                    +
                  </button>
                </div>
              </div>
            )}

            <div className="pd-main-actions">

              <button
                className="pd-add-to-bag"
                onClick={() =>
                  addToCart(
                    product.id,
                    quantity
                  )
                }
                disabled={
                  currentStock <= 0 || cartQty >= currentStock
                }
                style={{
                  opacity: currentStock <= 0 || cartQty >= currentStock ? 0.65 : 1,
                  cursor: currentStock <= 0 || cartQty >= currentStock ? "not-allowed" : "pointer",
                }}
              >
                <span>
                  {currentStock <= 0 ? "✕" : cartQty >= currentStock ? "✓" : "👜"}
                </span>

                {currentStock <= 0
                  ? "Out of Stock"
                  : cartQty >= currentStock
                  ? `In Bag (${cartQty})`
                  : cartQty > 0
                  ? `Add More to Bag (${quantity})`
                  : "Add to Bag"}
              </button>

              <button
                className={`pd-icon-btn ${
                  wishlisted
                    ? "active-wishlist"
                    : ""
                }`}
                title="Wishlist"
                onClick={
                  addWishlist
                }
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill={
                    wishlisted
                      ? "#8b263e"
                      : "none"
                  }
                  stroke={
                    wishlisted
                      ? "#8b263e"
                      : "currentColor"
                  }
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </button>

              <button
                className="pd-icon-btn"
                title="Share"
                onClick={() => {
                  if (
                    navigator.share
                  ) {
                    navigator.share(
                      {
                        title:
                          product.name,
                        url:
                          window.location
                            .href,
                      }
                    );
                  } else {
                    navigator.clipboard.writeText(
                      window.location
                        .href
                    );

                    alert(
                      "Link copied to clipboard!"
                    );
                  }
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle
                    cx="18"
                    cy="5"
                    r="3"
                  />

                  <circle
                    cx="6"
                    cy="12"
                    r="3"
                  />

                  <circle
                    cx="18"
                    cy="19"
                    r="3"
                  />

                  <line
                    x1="8.59"
                    y1="13.51"
                    x2="15.42"
                    y2="17.49"
                  />

                  <line
                    x1="15.41"
                    y1="6.51"
                    x2="8.59"
                    y2="10.49"
                  />
                </svg>
              </button>

            </div>

            <button
              className="pd-buy-now"
              onClick={
                handleBuyNow
              }
              disabled={
                currentStock <= 0
              }
              style={{
                opacity: currentStock <= 0 ? 0.6 : 1,
                cursor: currentStock <= 0 ? "not-allowed" : "pointer",
              }}
            >
              {currentStock <= 0 ? "Currently Out of Stock" : "⚡ Proceed to Checkout"}
            </button>

          </div>

          {/* ==================================================
              TRUST
          ================================================== */}

          <div className="pd-trust">
            <div>
              🛡️ 100% Handloom Certified
            </div>

            <div>
              🚚 Free Insured Shipping
            </div>

            <div>
              🔄 7-Day Easy Returns
            </div>
          </div>

          {/* ==================================================
              TABS
          ================================================== */}

          <div className="pd-tabs">

            <button
              className={
                activeTab ===
                "description"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setActiveTab(
                  "description"
                )
              }
            >
              Description
            </button>

            <button
              className={
                activeTab === "care"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setActiveTab("care")
              }
            >
              Care & Weave
            </button>

            <button
              className={
                activeTab ===
                "reviews"
                  ? "active"
                  : ""
              }
              onClick={() =>
                setActiveTab(
                  "reviews"
                )
              }
            >
              Reviews ({totalReviews})
            </button>

          </div>

          <div className="pd-tab-content">

            {activeTab ===
              "description" && (
              <p className="tab-text">
                {product.description ||
                  "Exquisite drape woven with genuine zari threads, reflecting a majestic heritage aesthetic crafted for elite celebrations."}
              </p>
            )}

            {activeTab ===
              "care" && (
              <p className="tab-text">
                Dry clean only. Store
                your handloom saree
                wrapped in clean
                muslin cloth in a dry,
                cool place. Air
                occasionally away from
                direct sunlight.
              </p>
            )}

            {activeTab ===
              "reviews" && (
              <div className="reviews-section">

                <div className="review-form">

                  <h3>
                    Leave Your Feedback
                  </h3>

                  <div className="star-input">

                    {[1, 2, 3, 4, 5].map(
                      (star) => (
                        <span
                          key={star}
                          className={`star ${
                            star <=
                            newReview.rating
                              ? "active-star"
                              : ""
                          }`}
                          onClick={() =>
                            setNewReview({
                              ...newReview,
                              rating:
                                star,
                            })
                          }
                        >
                          ★
                        </span>
                      )
                    )}

                  </div>

                  <textarea
                    placeholder="Share your experience with this product..."
                    value={
                      newReview.comment
                    }
                    onChange={(e) =>
                      setNewReview({
                        ...newReview,
                        comment:
                          e.target.value,
                      })
                    }
                  />

                  <button
                    className="submit-review-btn"
                    onClick={
                      handleAddReview
                    }
                  >
                    Post Review
                  </button>

                </div>

                <div className="review-list">

                  {reviews.length ===
                    0 && (
                    <p className="no-reviews">
                      No reviews for this
                      product yet. Be
                      the first to share
                      your experience!
                    </p>
                  )}

                  {reviews.map(
                    (rev) => (
                      <div
                        key={rev.id}
                        className="review-card"
                      >
                        <div className="review-rating">
                          {"★".repeat(
                            Number(
                              rev.rating ||
                                0
                            )
                          )}

                          {"☆".repeat(
                            5 -
                              Number(
                                rev.rating ||
                                  0
                              )
                          )}
                        </div>

                        <p className="review-comment">
                          {
                            rev.comment
                          }
                        </p>
                      </div>
                    )
                  )}

                </div>

              </div>
            )}

          </div>
        </div>
      </div>

      {/* ======================================================
          RELATED PRODUCTS
      ====================================================== */}

      {relatedProducts.length >
        0 && (
        <section className="related-section">

          <p className="related-subtitle">
            COMPLETE YOUR WARDROBE
          </p>

          <h2 className="related-title">
            You May Also Like
          </h2>

          <div className="related-grid">

            {relatedProducts.map(
              (item) => {

                const relatedDiscount =
                  item.old_price
                    ? Math.round(
                        ((item.old_price -
                          item.price) /
                          item.old_price) *
                          100
                      )
                    : 0;

                return (
                  <div
                    key={item.id}
                    className="related-card-wrapper"
                  >
                    <Link
                      to={`/product/${item.id}`}
                      className="related-card"
                    >

                      <div className="related-img-container">

                        <img
                          src={
                            item.img_url ||
                            item.variants?.[0]?.mainImage ||
                            item.thumbnails?.[0] ||
                            ""
                          }
                          alt={
                            item.name
                          }
                        />

                        <div className="related-badges">

                          {relatedDiscount >
                            0 && (
                            <span className="badge-discount">
                              -
                              {
                                relatedDiscount
                              }
                              %
                            </span>
                          )}

                        </div>
                      </div>

                      <div className="related-info">

                        <p className="related-item-cat">
                          {item.category ||
                            "Saree"}
                        </p>

                        <h3 className="related-item-name">
                          {item.name}
                        </h3>

                        <div className="related-price-row">

                          <span className="related-curr-price">
                            ₹
                            {Number(
                              item.price
                            ).toLocaleString()}
                          </span>

                          {item.old_price && (
                            <span className="related-old-price">
                              ₹
                              {Number(
                                item.old_price
                              ).toLocaleString()}
                            </span>
                          )}

                        </div>
                      </div>

                    </Link>
                  </div>
                );
              }
            )}

          </div>
        </section>
      )}
    </div>
  );
};

// ============================================================
// STYLES
// ============================================================

const styles = `

.pd-container {
  padding: 40px 6%;
  font-family: 'Playfair Display', serif, sans-serif;
  background: linear-gradient(135deg, #f7f2f9 0%, #f3ebf5 100%);
  color: #3b1443;
  min-height: 100vh;
}

.pd-loading {
  text-align: center;
  padding: 100px;
  font-size: 20px;
  color: #6a2e7c;
  background: #f7f2f9;
  min-height: 100vh;
}

.pd-breadcrumb {
  margin-bottom: 25px;
  color: #6a2e7c;
  font-size: 14px;
}

.pd-breadcrumb a {
  color: #6a2e7c;
  text-decoration: none;
}

.pd-breadcrumb span {
  color: #3b1443;
  font-weight: 600;
}

.pd-wrapper {
  display: flex;
  gap: 50px;
  flex-wrap: wrap;
}

.pd-left,
.pd-right {
  flex: 1 1 450px;
}

.pd-main-img-container {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(74, 25, 89, 0.15);
  background: #fff;
  border: 1px solid rgba(106, 46, 124, 0.2);
}

.pd-main-img {
  width: 100%;
  height: 520px;
  object-fit: cover;
  display: block;
  transition: transform 0.5s ease;
}

.pd-main-img:hover {
  transform: scale(1.03);
}

.pd-floating-badge {
  position: absolute;
  top: 20px;
  left: 20px;
  background: linear-gradient(135deg, #8b263e, #6b1b2f);
  color: #fff;
  padding: 6px 14px;
  border-radius: 30px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
}

.pd-thumbnails {
  display: flex;
  gap: 12px;
  margin-top: 20px;
  overflow-x: auto;
  padding-bottom: 5px;
}

.pd-thumb {
  width: 80px;
  height: 90px;
  flex: 0 0 80px;
  border-radius: 12px;
  object-fit: cover;
  cursor: pointer;
  border: 2px solid rgba(106, 46, 124, 0.2);
  transition: all 0.2s ease;
  box-shadow: 0 4px 10px rgba(74, 25, 89, 0.08);
}

.pd-thumb:hover {
  transform: translateY(-2px);
}

.pd-thumb.active-thumb {
  border-color: #6a2e7c;
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(106, 46, 124, 0.25);
}

.pd-category {
  color: #6a2e7c;
  letter-spacing: 3px;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.pd-title {
  font-size: 36px;
  color: #3b1443;
  margin: 0 0 15px 0;
  font-weight: 700;
  line-height: 1.2;
}

.pd-rating {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.stars {
  color: #d4af37;
  font-size: 18px;
  letter-spacing: 2px;
}

.rating-text {
  font-size: 14px;
  color: #6e4878;
}

.pd-price-row {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.pd-price {
  font-size: 32px;
  font-weight: 800;
  color: #3b1443;
}

.pd-old {
  text-decoration: line-through;
  color: #896d91;
  font-size: 18px;
}

.pd-discount {
  background: rgba(106, 46, 124, 0.12);
  color: #6a2e7c;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  border: 1px solid rgba(106, 46, 124, 0.25);
}

/* ============================================================
   VARIANTS
============================================================ */

.pd-variant-section,
.pd-size-section {
  margin: 20px 0;
  padding: 18px;
  background: rgba(255, 255, 255, 0.72);
  border-radius: 16px;
  border: 1px solid rgba(106, 46, 124, 0.18);
}

.pd-variant-heading {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  color: #3b1443;
  font-size: 15px;
}

.pd-variant-heading span {
  font-weight: 600;
}

.pd-variant-heading strong {
  color: #6a2e7c;
  text-transform: capitalize;
}

/* Colour buttons */

.pd-colour-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.pd-colour-option {
  min-width: 95px;
  padding: 7px;
  border-radius: 14px;
  background: #fff;
  border: 2px solid rgba(106, 46, 124, 0.18);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  transition: all 0.25s ease;
}

.pd-colour-option:hover {
  transform: translateY(-2px);
  border-color: #6a2e7c;
}

.pd-colour-option.selected-colour {
  border-color: #6a2e7c;
  box-shadow: 0 5px 16px rgba(106, 46, 124, 0.25);
  background: #faf6fc;
}

.pd-colour-image-wrap {
  width: 58px;
  height: 58px;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eee;
}

.pd-colour-image-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pd-colour-dot {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 2px solid rgba(0,0,0,0.1);
}

.pd-colour-name {
  font-size: 12px;
  color: #3b1443;
  font-weight: 700;
  text-transform: capitalize;
}

/* Size buttons */

.pd-size-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.pd-size-btn {
  min-width: 52px;
  padding: 10px 18px;
  background: #fff;
  color: #3b1443;
  border: 1.5px solid rgba(106, 46, 124, 0.22);
  border-radius: 999px;
  cursor: pointer;
  font-weight: 700;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.pd-size-btn:hover:not(:disabled) {
  border-color: #6a2e7c;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(106, 46, 124, 0.15);
}

.pd-size-btn.selected-size {
  background: linear-gradient(135deg, #5c2069 0%, #3d1445 100%);
  color: #fbeea4;
  border-color: #d4af37;
  box-shadow: 0 4px 16px rgba(92, 32, 105, 0.3);
}

.pd-size-btn.size-out-stock {
  color: #aaa;
  background: #f3f3f3;
  text-decoration: line-through;
  cursor: not-allowed;
}

/* ============================================================
   STOCK
============================================================ */

.pd-stock {
  margin-bottom: 20px;
}

.stock-badge {
  font-size: 14px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 30px;
  display: inline-block;
}

.stock-badge.in-stock {
  background: rgba(46, 125, 50, 0.1);
  color: #2e7d32;
  border: 1px solid rgba(46, 125, 50, 0.2);
}

.stock-badge.out-stock {
  background: rgba(198, 40, 40, 0.1);
  color: #c62828;
  border: 1px solid rgba(198, 40, 40, 0.2);
}

.pd-divider {
  margin: 20px 0;
  border: none;
  border-top: 1px solid rgba(106, 46, 124, 0.2);
}

.pd-spec-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 25px;
}

.pd-spec-box {
  background: linear-gradient(135deg, #ffffff 0%, #f9f5fb 100%);
  padding: 16px;
  border-radius: 14px;
  box-shadow: 0 4px 15px rgba(74, 25, 89, 0.06);
  border: 1px solid rgba(106, 46, 124, 0.18);
}

.pd-spec-box span {
  font-size: 11px;
  color: #6a2e7c;
  letter-spacing: 1.5px;
  font-weight: 700;
}

.pd-spec-box p {
  margin: 6px 0 0 0;
  font-size: 15px;
  font-weight: 600;
  color: #3b1443;
}

.pd-qty-row {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 25px;
}

.pd-qty-label {
  font-weight: 600;
  color: #3b1443;
}

.pd-qty {
  display: flex;
  align-items: center;
  gap: 20px;
  border: 1px solid rgba(106, 46, 124, 0.35);
  border-radius: 30px;
  padding: 8px 20px;
  background: #fff;
  color: #3b1443;
  box-shadow: 0 2px 8px rgba(74, 25, 89, 0.05);
}

.pd-qty button {
  border: none;
  background: none;
  font-size: 18px;
  cursor: pointer;
  color: #6a2e7c;
  font-weight: bold;
}

.pd-qty button:disabled {
  color: #ccc;
  cursor: not-allowed;
}

.pd-action-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 25px;
}

.pd-main-actions {
  display: flex;
  gap: 12px;
}

.pd-add-to-bag {
  flex: 1;
  background: linear-gradient(135deg, #5c2069, #45154f);
  color: #fff;
  padding: 16px;
  border-radius: 40px;
  border: none;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  box-shadow: 0 6px 20px rgba(92, 32, 105, 0.3);
  transition: all 0.3s ease;
}

.pd-add-to-bag:hover:not(:disabled) {
  background: linear-gradient(135deg, #6a2e7c, #5c2069);
  transform: translateY(-2px);
}

.pd-add-to-bag:disabled {
  background: #d8cce0;
  color: #888;
  cursor: not-allowed;
}

.pd-icon-btn {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 1px solid rgba(106, 46, 124, 0.35);
  background: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: #5c2069;
  transition: all 0.2s ease;
  box-shadow: 0 4px 10px rgba(74, 25, 89, 0.08);
}

.pd-icon-btn:hover {
  background: #f4ecf7;
  border-color: #5c2069;
  transform: translateY(-2px);
}

.pd-buy-now {
  width: 100%;
  background: linear-gradient(135deg, #d4af37 0%, #aa8c2c 100%);
  color: #3b1443;
  padding: 16px;
  border-radius: 40px;
  border: 1px solid #f3e5ab;
  font-weight: 800;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(212, 175, 55, 0.4);
  transition: all 0.3s ease;
}

.pd-buy-now:hover:not(:disabled) {
  background: linear-gradient(135deg, #e2be42, #b89832);
  transform: translateY(-2px);
}

.pd-buy-now:disabled {
  background: #d8cce0;
  color: #888;
  border: none;
  cursor: not-allowed;
  box-shadow: none;
}

.pd-trust {
  display: flex;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #ffffff 0%, #f9f5fb 100%);
  border-radius: 14px;
  font-size: 13px;
  color: #5c2069;
  font-weight: 600;
  margin-bottom: 25px;
  border: 1px solid rgba(106, 46, 124, 0.18);
}

.pd-tabs {
  display: flex;
  background: #e6d8ec;
  border-radius: 30px;
  padding: 4px;
  margin-bottom: 15px;
}

.pd-tabs button {
  flex: 1;
  padding: 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-weight: 600;
  color: #6e4878;
  border-radius: 25px;
}

.pd-tabs button.active {
  background: #5c2069;
  color: #fff;
}

.pd-tab-content {
  background: #fff;
  padding: 25px;
  border-radius: 16px;
  box-shadow: 0 6px 25px rgba(74, 25, 89, 0.08);
  min-height: 140px;
  border: 1px solid rgba(106, 46, 124, 0.18);
}

.tab-text {
  line-height: 1.7;
  color: #451b4f;
}

.review-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 25px;
}

.review-form h3 {
  margin: 0;
  color: #3b1443;
  font-size: 18px;
}

.star-input {
  font-size: 30px;
  cursor: pointer;
  display: flex;
  gap: 6px;
}

.star {
  color: #d8c5df;
}

.active-star {
  color: #d4af37;
}

.review-form textarea {
  min-height: 90px;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid rgba(106, 46, 124, 0.3);
  background: #f9f5fb;
  color: #3b1443;
  font-family: inherit;
  resize: vertical;
}

.submit-review-btn {
  background: #5c2069;
  color: #fff;
  padding: 12px 24px;
  border: none;
  border-radius: 30px;
  font-weight: 700;
  cursor: pointer;
  width: fit-content;
}

.review-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.review-card {
  background: #f9f5fb;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid rgba(106, 46, 124, 0.18);
}

.review-rating {
  color: #d4af37;
  margin-bottom: 6px;
}

.review-comment {
  margin: 0;
  color: #3b1443;
  font-size: 14px;
}

.no-reviews {
  color: #6a2e7c;
  font-style: italic;
}

/* ============================================================
   RELATED PRODUCTS
============================================================ */

.related-section {
  margin-top: 80px;
  text-align: center;
}

.related-subtitle {
  color: #6a2e7c;
  letter-spacing: 3px;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 8px;
}

.related-title {
  font-size: 38px;
  color: #3b1443;
  margin-bottom: 40px;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 25px;
  text-align: left;
}

.related-card-wrapper {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 6px 20px rgba(74, 25, 89, 0.08);
  border: 1px solid rgba(106, 46, 124, 0.18);
  transition: transform 0.3s ease;
}

.related-card-wrapper:hover {
  transform: translateY(-5px);
}

.related-card {
  text-decoration: none;
  color: inherit;
  display: block;
}

.related-img-container {
  position: relative;
  height: 320px;
  overflow: hidden;
}

.related-img-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.related-badges {
  position: absolute;
  top: 15px;
  left: 15px;
}

.badge-discount {
  background: #8b263e;
  color: white;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: bold;
}

.related-info {
  padding: 18px;
}

.related-item-cat {
  font-size: 11px;
  color: #6a2e7c;
  margin-bottom: 4px;
  letter-spacing: 1px;
  text-transform: uppercase;
  font-weight: 600;
}

.related-item-name {
  font-size: 17px;
  color: #3b1443;
  margin-bottom: 10px;
  font-weight: 600;
}

.related-price-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.related-curr-price {
  font-size: 18px;
  font-weight: 700;
  color: #3b1443;
}

.related-old-price {
  font-size: 13px;
  color: #9c7aa6;
  text-decoration: line-through;
}

/* ============================================================
   MOBILE
============================================================ */

@media(max-width: 768px) {

  .pd-container {
    padding: 25px 4%;
  }

  .pd-wrapper {
    flex-direction: column;
    gap: 30px;
  }

  .pd-title {
    font-size: 28px;
  }

  .pd-price {
    font-size: 26px;
  }

  .pd-main-img {
    height: 430px;
  }

  .pd-spec-grid {
    grid-template-columns: 1fr 1fr;
  }

  .pd-trust {
    flex-direction: column;
    gap: 10px;
  }

  .related-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }

  .related-img-container {
    height: 220px;
  }

  .pd-colour-list {
    gap: 8px;
  }

  .pd-colour-option {
    min-width: 82px;
  }

}
`;

export default ProductdetailedPage;